import { CanonicalWine } from '../types';
import { getWineDisplayName, hasDistinctName } from '../utils';

interface CanonicalWineDetailProps {
  wine: CanonicalWine;
  onClose: () => void;
}

export function CanonicalWineDetail({ wine, onClose }: CanonicalWineDetailProps) {
  const vivinoScore = wine.vivino_rating;
  const otherScores = wine.critic_scores || [];
  const displayName = getWineDisplayName(wine);
  const showProducerLine = hasDistinctName(wine);

  const formatLastUpdated = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center sm:p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div 
        className="w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl flex flex-col overflow-hidden"
        style={{ background: '#f5f1e8' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: '#e8e4da', background: '#faf8f4' }}>
          <button
            onClick={onClose}
            className="flex items-center gap-1 text-sm"
            style={{ color: '#722f37' }}
          >
            <span>←</span> Back
          </button>
          <span className="text-xs px-2 py-1 rounded" style={{ background: '#e8e4da', color: '#8b7d6b' }}>
            From History
          </span>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Hero Section */}
          <div className="p-6 text-center border-b" style={{ borderColor: '#e8e4da', background: '#faf8f4' }}>
            <h1 className="text-2xl wine-title mb-1" style={{ color: '#722f37' }}>
              {displayName} {wine.vintage && <span className="font-light">{wine.vintage}</span>}
            </h1>
            {showProducerLine && (
              <p className="text-lg" style={{ color: '#5d4e37' }}>{wine.producer}</p>
            )}
          </div>

          {/* Ratings Section */}
          <div className="p-4 border-b" style={{ borderColor: '#e8e4da' }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm uppercase tracking-wide" style={{ color: '#8b7d6b' }}>Ratings</h2>
                {wine.ratings_updated_at && (
                  <p className="text-xs" style={{ color: '#a99d8c' }}>
                    Updated {formatLastUpdated(wine.ratings_updated_at)}
                  </p>
                )}
              </div>
            </div>
            {(vivinoScore || otherScores.length > 0) ? (
              <div className="space-y-2">
                {vivinoScore && (
                  <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: '#faf8f4' }}>
                    <span style={{ color: '#5d4e37' }}>Vivino</span>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-semibold" style={{ color: '#722f37' }}>
                        {vivinoScore.toFixed(1)}
                      </span>
                      <span style={{ color: '#722f37' }}>★</span>
                    </div>
                  </div>
                )}
                {otherScores.map(score => (
                  <div key={score.id} className="flex justify-between items-center p-3 rounded-lg" style={{ background: '#faf8f4' }}>
                    <span style={{ color: '#5d4e37' }}>{score.critic_name}</span>
                    <span className="text-lg font-semibold" style={{ color: '#722f37' }}>
                      {score.score}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: '#8b7d6b' }}>No ratings available</p>
            )}
          </div>

          {/* Critic Notes */}
          <div className="p-4 border-b" style={{ borderColor: '#e8e4da' }}>
            <h2 className="text-sm uppercase tracking-wide mb-3" style={{ color: '#8b7d6b' }}>Critic Notes</h2>
            {wine.critic_notes ? (
              <p className="text-sm italic" style={{ color: '#5d4e37' }}>"{wine.critic_notes}"</p>
            ) : (
              <p className="text-sm" style={{ color: '#8b7d6b' }}>No critic notes available</p>
            )}
          </div>

          {/* Wine Details */}
          <div className="p-4 border-b" style={{ borderColor: '#e8e4da' }}>
            <h2 className="text-sm uppercase tracking-wide mb-3" style={{ color: '#8b7d6b' }}>Details</h2>
            <div className="space-y-2">
              {wine.varietal && (
                <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e8e4da' }}>
                  <span style={{ color: '#8b7d6b' }}>Varietal</span>
                  <span style={{ color: '#5d4e37' }}>{wine.varietal}</span>
                </div>
              )}
              {wine.region && (
                <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e8e4da' }}>
                  <span style={{ color: '#8b7d6b' }}>Region</span>
                  <span style={{ color: '#5d4e37' }}>{wine.region}</span>
                </div>
              )}
              {wine.appellation && (
                <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e8e4da' }}>
                  <span style={{ color: '#8b7d6b' }}>Appellation</span>
                  <span style={{ color: '#5d4e37' }}>{wine.appellation}</span>
                </div>
              )}
              {wine.country && (
                <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e8e4da' }}>
                  <span style={{ color: '#8b7d6b' }}>Country</span>
                  <span style={{ color: '#5d4e37' }}>{wine.country}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e8e4da' }}>
                <span style={{ color: '#8b7d6b' }}>Color</span>
                <span className="capitalize" style={{ color: '#5d4e37' }}>{wine.color}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom - Close button */}
        <div className="p-4 border-t flex-shrink-0 safe-bottom" style={{ borderColor: '#e8e4da', background: '#faf8f4' }}>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-lg font-medium"
            style={{ background: '#e8e4da', color: '#5d4e37' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
